import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useRole } from '../../context/RoleContext';
import { useOnboardingState } from '../../hooks/useOnboardingState';
import { WelcomeCarousel } from './WelcomeCarousel';
import { reportError } from '../../lib/errors';

/**
 * Decides whether the welcome carousel runs, and gets out of the way otherwise.
 *
 * Rendered from the layout rather than the dashboard page so a new user meets
 * it wherever they happen to land — a deep link from the welcome email should
 * not silently skip their first run.
 *
 * Owner-only for now: the carousel's core slide is Safe-to-Spend, which the
 * mechanic and member roles can't open. Orientation for invited staff wants its
 * own shorter script, and until that's written the Help Centre — which does
 * filter itself by role — is the better surface for them.
 */
export const FirstRun = () => {
    const { can, loading: roleLoading } = useRole();
    const { welcomeDone, loading: stateLoading, patch } = useOnboardingState();
    const [firstName, setFirstName] = useState<string | undefined>();

    const eligible = !roleLoading && !stateLoading && !welcomeDone && can('dashboard');

    useEffect(() => {
        if (!eligible) return;
        let active = true;

        (async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data, error: fetchError } = await supabase
                .from('profiles')
                .select('full_name')
                .eq('id', user.id)
                .single();
            if (fetchError) reportError(fetchError, 'first-run.profiles');
            if (active && data?.full_name) setFirstName(data.full_name.split(' ')[0]);
        })();

        return () => { active = false; };
    }, [eligible]);

    if (!eligible) return null;

    return (
        <WelcomeCarousel
            userName={firstName}
            onComplete={() => patch({ welcome_completed_at: new Date().toISOString() })}
            onSkip={() => patch({ welcome_skipped: true })}
        />
    );
};
